import {
  MixpanelEvent,
  MixpanelEventSource,
  MixpanelSourceProvider,
  tabLabelerEnumerated,
  useMixpanel,
} from '@talos/kyoko';
import { memo, useCallback, useMemo } from 'react';
import { DEFAULT_TRANSFERS_TABS, NEW_TRANSFER_TAB_DEFAULTS } from '../../../Blotters/Transfers/defaults';
import { TRANSFERS_BLOTTER_PREFIX } from '../../../Blotters/Transfers/tokens';
import type { TransfersTabProps } from '../../../Blotters/Transfers/TransfersBlotter';
import {
  MobileBlotterPanel,
  type MobileBlotterPanelAddTabOption,
  type MobileBlotterPanelProps,
  type MobileBlotterPanelRenderTabArgs,
} from './MobileBlotterPanel';
import { MobileFilteredTransfers } from './Transfers/MobileFilteredTransfers';

export const MobileTransfersBlotter = memo(function MobileTransfersBlotter(
  props: Omit<MobileBlotterPanelProps<TransfersTabProps>, 'defaultTabs' | 'renderTab'>
) {
  const mixpanel = useMixpanel(MixpanelEventSource.TransferBlotter);
  const tabLabeler = useMemo(() => tabLabelerEnumerated<TransfersTabProps>('Transfers'), []);
  const renderTab = useCallback(({ tab, tabId }: MobileBlotterPanelRenderTabArgs<TransfersTabProps>) => {
    return (
      <MobileFilteredTransfers
        key={tabId}
        blotterID={`${TRANSFERS_BLOTTER_PREFIX}/${tabId}`}
        defaultFilter={tab.defaultFilter ?? NEW_TRANSFER_TAB_DEFAULTS.defaultFilter}
        defaultColumns={tab.defaultColumns}
      />
    );
  }, []);

  const addTabOptions = useMemo<MobileBlotterPanelAddTabOption<TransfersTabProps>[]>(
    () => [
      {
        id: 'transfers',
        label: 'Transfers',
        createTab: sourceTabs => {
          const newTab = { ...NEW_TRANSFER_TAB_DEFAULTS };
          return {
            ...newTab,
            label: tabLabeler(newTab, [...sourceTabs], 'creation'),
          };
        },
        onAdd: () => mixpanel.track(MixpanelEvent.AddTab),
      },
    ],
    [mixpanel, tabLabeler]
  );

  return (
    <MixpanelSourceProvider value={MixpanelEventSource.TransferBlotter}>
      <MobileBlotterPanel<TransfersTabProps>
        {...props}
        defaultTabs={DEFAULT_TRANSFERS_TABS}
        renderTab={renderTab}
        addTabOptions={addTabOptions}
      />
    </MixpanelSourceProvider>
  );
});
